import { MutableRefObject, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import DefaultSpinner from "../../../../components/LoadingSpinner/default-spinner.component";
import { Timeframe } from "../../../../features/FloorManager/typings/shared/timeframe";
import { finishInit } from "../../../../functions/FacilityManagerInitializer";
import { useRemoteFetchFloorPlan } from "../../../../hooks/Remote/FloorPlan/useRemoteFetchFloorPlan";
import currentPlanUniqueTimeframesSelector from "../../selectors/currentPlanUniqueTimeframesSelector";
import selectedEntitiesSelector from "../../selectors/selectedEntitiesSelector";
import InitializingLoadingScreenModal from "../initializing-loadingscreen.component";
import { useRemoteFetchFloorPlanService } from "../../../../hooks/Remote/FloorPlan/useRemoteFetchFloorPlanService";
import { TimeframesList } from "./menu-timeframes-list.component";

type Props = {
  floorTimeframes: Timeframe[];
  refetchFloorTimeframes: () => void;
};

const TimeframesMenu: React.FC<Props> = ({ floorTimeframes, refetchFloorTimeframes }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const selected = useSelector(selectedEntitiesSelector);
  const uniqueTimeframes = useSelector(currentPlanUniqueTimeframesSelector);
  const { isInitializing: isInitializingPlan } = useSelector(
    (state: RootState) => state.floorManager.floors
  );

  const [newTimeframe, setNewTimeframe] = useState(false);
  const [startDate, setStartDate] = useState<DateTime | null>(null);

  const [selectedTimeframe, setSelectedTimeframe] = useState<Timeframe>({ start: "", end: "" });
  const [newTimeframeData, setNewTimeframeData] = useState<Timeframe>({ start: "", end: "" });

  const [isCreateFirstTimeframe, setIsCreateFirstTimeframe] = useState(false);
  const [isEditTimeframe, setIsEditTimeframe] = useState(false);
  const [isContinueTimeframe, setIsContinueTimeframe] = useState(false);

  const [isEditService, setIsEditService] = useState(false);
  const shouldFetchNormalFloorPlan =
    !isEditService && (isEditTimeframe || isContinueTimeframe || isCreateFirstTimeframe);

  const { data: floorPlan, isFetching: floorPlanIsFetching } = useRemoteFetchFloorPlan(
    selected.floor?.id || 0,
    selectedTimeframe.start,
    selectedTimeframe.end,
    shouldFetchNormalFloorPlan
  );

  const { data: floorPlanForService, isFetching: floorPlanForServiceIsFetching } =
    useRemoteFetchFloorPlanService(
      selected.floor?.id || 0,
      selectedTimeframe.start,
      null,
      isEditService
    );

  const clickEdit = async (timeframe: Timeframe) => {
    setSelectedTimeframe(timeframe);
    setIsEditService(false);
    setIsCreateFirstTimeframe(false);
    setIsContinueTimeframe(false);
    setIsEditTimeframe(true);
  };

  const editServices = (timeframe: Timeframe) => {
    setSelectedTimeframe(timeframe);
    setIsEditTimeframe(false);
    setIsCreateFirstTimeframe(false);
    setIsContinueTimeframe(false);
    setIsEditService(true);
  };

  /**
   * @argument selectedTimeframe_for_floorplan
   * when timeframe is existed and create new one,
   * it used to select the latest one from the existing by setSelectedTimeframe(floorTimeframes.slice(-1)[0])
   * but now, BE returns the latest
   */
  const clickNextCreate = (startDay: string) => {
    setSelectedTimeframe({ start: startDay, end: null });
    setNewTimeframeData({ start: startDay, end: null });
    if (floorTimeframes.length) setIsContinueTimeframe(true);
    else setIsCreateFirstTimeframe(true);
  };

  useEffect(() => {
    // edit the floor plan only for services
    if (isEditService && !floorPlanForServiceIsFetching && floorPlanForService) {
      finishInit(dispatch, selectedTimeframe, floorPlanForService);
      setIsEditService(false);
    }
  }, [
    isEditService,
    floorPlanForServiceIsFetching,
    floorPlanForService,
    selectedTimeframe,
    dispatch
  ]);

  useEffect(() => {
    // show plan or edit the last time frame
    if (!isEditService && isEditTimeframe && !floorPlanIsFetching && floorPlan) {
      finishInit(dispatch, selectedTimeframe, floorPlan);
      setIsEditTimeframe(false);
    }
  }, [isEditService, isEditTimeframe, floorPlanIsFetching, floorPlan, dispatch, selectedTimeframe]);

  useEffect(() => {
    // timeframe is existed but create new one
    if (!isEditService && isContinueTimeframe && !floorPlanIsFetching && floorPlan) {
      finishInit(dispatch, newTimeframeData, floorPlan);
      setIsContinueTimeframe(false);
    }
  }, [
    isEditService,
    isContinueTimeframe,
    newTimeframeData,
    floorPlanIsFetching,
    floorPlan,
    dispatch
  ]);

  useEffect(() => {
    // initial the very first timeframe
    if (!isEditService && isCreateFirstTimeframe && floorPlan) {
      finishInit(dispatch, newTimeframeData, floorPlan);
      setIsCreateFirstTimeframe(false);
    }
  }, [isEditService, isCreateFirstTimeframe, newTimeframeData, floorPlan, dispatch]);

  if (!uniqueTimeframes || !selected.floor) return <DefaultSpinner />;
  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      data-testid="menu-timeframe-box"
    >
      <InitializingLoadingScreenModal
        isOpen={isInitializingPlan}
        title={t("Calculating basis for your next plan...")}
      />

      <TimeframesList
        timeframes={floorTimeframes}
        selectedFloor={selected.floor}
        isEditService={isEditService}
        isEditTimeframe={isEditTimeframe}
        startDate={startDate}
        newTimeframe={newTimeframe}
        refetchFloorTimeframes={refetchFloorTimeframes}
        clickEdit={clickEdit}
        editServices={editServices}
        setNewTimeframe={setNewTimeframe}
        setStartDate={setStartDate}
        clickNextCreate={clickNextCreate}
      />
    </Box>
  );
};

export default TimeframesMenu;

/** Call when user selected the same timeframe (prev matches selected). Applies the selection. */
export function applyTimeframeSelectionWhenSame(
  previousSelectedTimeframe: MutableRefObject<Timeframe>,
  selectedTimeframe: Timeframe,
  selectTimeframe: () => void
) {
  const prev = previousSelectedTimeframe.current;
  const isSame = prev.start == selectedTimeframe.start || prev.end == selectedTimeframe.end;
  if (isSame) {
    selectTimeframe();
  } else {
    previousSelectedTimeframe.current = selectedTimeframe;
  }
}

/** Call when user is selecting a new timeframe. Applies the selection. */
export function applyTimeframeSelectionWhenNew(selectTimeframe: () => void) {
  selectTimeframe();
}

export function checkStartAndReturnTargetFrame(start: DateTime | null, end: DateTime | null) {
  if (!start) throw new Error("Timeframe needs start");

  const targetFrame = {
    start: start.toISO(),
    end: end?.toISO() ?? null
  };

  return targetFrame;
}
